import {
  Activity,
  CalendarDays,
  ChevronDown,
  Filter,
  Plus,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";
import { EventItem } from "../components/records";
import { Button, Card, EmptyState, PageHeader } from "../components/ui";
import { useApp } from "../context/AppContext";
import { eventLabels } from "../lib/format";

type TimelineRange = "30" | "90" | "ALL";

const PAGE_SIZE = 40;
const rangeOptions: Array<{ value: TimelineRange; label: string }> = [
  { value: "30", label: "近 30 天" },
  { value: "90", label: "近 90 天" },
  { value: "ALL", label: "已载入历史" }
];

export function TimelinePage() {
  const { data, openModal } = useApp();
  const [filter, setFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState<TimelineRange>("30");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  if (!data) return null;

  const rangeEvents = useMemo(() => {
    if (range === "ALL") return data.events;
    const cutoff = Date.now() - Number(range) * 86_400_000;
    return data.events.filter((event) => new Date(event.occurred_at).getTime() >= cutoff);
  }, [data.events, range]);

  const visible = useMemo(
    () =>
      rangeEvents.filter((event) => {
        const matchesType = filter === "ALL" || event.event_type === filter;
        const term = query.trim().toLowerCase();
        const matchesQuery =
          !term ||
          event.title.toLowerCase().includes(term) ||
          event.factual_content.toLowerCase().includes(term) ||
          event.result_summary.toLowerCase().includes(term);
        return matchesType && matchesQuery;
      }),
    [rangeEvents, filter, query]
  );

  const visibleEvents = visible.slice(0, visibleCount);
  const grouped = useMemo(() => {
    const map = new Map<string, typeof visibleEvents>();
    for (const event of visibleEvents) {
      const local = new Date(event.occurred_at);
      const key =
        local.getFullYear() +
        "-" +
        String(local.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(local.getDate()).padStart(2, "0");
      const current = map.get(key) ?? [];
      current.push(event);
      map.set(key, current);
    }
    return [...map.entries()];
  }, [visibleEvents]);

  const selectRange = (value: TimelineRange) => {
    setRange(value);
    setVisibleCount(PAGE_SIZE);
  };

  const selectType = (value: string) => {
    setFilter(value);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="page page--timeline">
      <PageHeader
        title="时间轴"
        description={(data.eventStats?.total ?? 0) > data.events.length
          ? `已载入最近 ${data.events.length} 条，共 ${data.eventStats!.total} 条事实。筛选仅作用于已载入记录；长期成长仍统计完整历史。`
          : "每一次推进、结果、决定和学习，都成为可追溯的成长事实。"}
        actions={<Button onClick={() => openModal("quick")} icon={<Plus size={16} />}>新增事项</Button>}
      />

      <Card className="toolbar-card">
        <div className="filter-search">
          <Search size={17} />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            placeholder="搜索标题、事实或结果"
            aria-label="搜索时间线"
          />
        </div>
        <div className="timeline-toolbar-groups">
          <div className="range-chips" aria-label="时间范围">
            {rangeOptions.map((option) => (
              <button
                key={option.value}
                aria-pressed={range === option.value}
                className={range === option.value ? "active" : ""}
                onClick={() => selectRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="filter-chips" aria-label="事实类型">
            <button aria-pressed={filter === "ALL"} className={filter === "ALL" ? "active" : ""} onClick={() => selectType("ALL")}>全部类型</button>
            {["PROGRESS", "RESULT", "ISSUE", "LEARNING", "DECISION"].map((type) => (
              <button key={type} aria-pressed={filter === type} className={filter === type ? "active" : ""} onClick={() => selectType(type)}>
                {eventLabels[type]}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <div className="timeline-layout">
        <Card className="timeline-card">
          {grouped.length ? (
            grouped.map(([date, events]) => (
              <div className="timeline-day" key={date}>
                <div className="timeline-day__heading">
                  <CalendarDays size={16} />
                  <strong>{formatDay(date)}</strong>
                  <span>{events.length} 条事实</span>
                </div>
                <div>
                  {events.map((event, index) => (
                    <EventItem event={event} key={event.id} last={index === events.length - 1} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <EmptyState
              icon={<Activity size={22} />}
              title={data.events.length ? "当前范围内没有符合条件的记录" : "时间轴从第一条真实行动开始"}
              description={
                data.events.length
                  ? "调整时间范围、事实类型，或搜索事实描述中的关键词。"
                  : "不必写成长文，一句“做了什么、结果如何、下一步是什么”就够了。"
              }
              actionLabel={data.events.length ? undefined : "新增第一项事项"}
              onAction={data.events.length ? undefined : () => openModal("quick")}
            />
          )}
          {visible.length > visibleEvents.length && (
            <div className="timeline-load-more">
              <span>已显示 {visibleEvents.length} / {visible.length} 条</span>
              <Button variant="secondary" size="sm" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                再显示 {Math.min(PAGE_SIZE, visible.length - visibleEvents.length)} 条
              </Button>
            </div>
          )}
        </Card>

        <aside className="timeline-aside">
          <Card className="timeline-summary">
            <span className="timeline-summary__label">当前范围</span>
            <div className="timeline-summary__metric">
              <strong>{rangeEvents.length}</strong>
              <span>条真实事实</span>
            </div>
            <div className="summary-bars">
              {["PROGRESS", "RESULT", "LEARNING", "ISSUE"].map((type) => {
                const count = rangeEvents.filter((event) => event.event_type === type).length;
                const width = rangeEvents.length ? (count / rangeEvents.length) * 100 : 0;
                return (
                  <div key={type}>
                    <span>{eventLabels[type]} <b>{count}</b></span>
                    <div><i style={{ width: String(width) + "%" }} /></div>
                  </div>
                );
              })}
            </div>
            <p>统计只来自当前时间范围内已保存的事件，不包含草稿或虚构趋势。</p>
          </Card>
          <Card className="timeline-tip">
            <div><Filter size={17} /></div>
            <strong>一次记录，多处联动</strong>
            <p>关联项目、任务或技能后，这条事实会在对应页面成为证据。</p>
            <button onClick={() => openModal("quick")}>新增事项 <ChevronDown size={14} /></button>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function formatDay(date: string) {
  const target = new Date(date + "T00:00:00");
  const now = new Date();
  const localDate = (d: Date) =>
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0");
  const todayString = localDate(now);
  const yesterdayString = localDate(new Date(now.getTime() - 86_400_000));
  if (date === todayString) return "今天";
  if (date === yesterdayString) return "昨天";
  return new Intl.DateTimeFormat("zh-CN", {
    year: target.getFullYear() === now.getFullYear() ? undefined : "numeric",
    month: "long",
    day: "numeric",
    weekday: "short"
  }).format(target);
}
